import { Component, OnInit, OnDestroy, signal, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { TranslocoModule } from '@jsverse/transloco';
import { InboxService } from '../../services/inbox.service';
import { AuthService } from '../../services/auth.service';

interface TabItem {
  /** Clé i18n. Le HTML applique `| transloco`. */
  label: string;
  icon: string;
  route?: string;
  matchPaths: string[];
  badge?: 'unreadInbox';
  isMore?: boolean;
}

interface MoreItem {
  /** Clé i18n. */
  label: string;
  /** Clé i18n. */
  sub: string;
  icon: string;
  route: string;
  tone: 'primary' | 'accent' | 'violet' | 'cyan' | 'pink' | 'gray';
  /** Si présent, l'item n'est visible que pour ces rôles. */
  roles?: string[];
}

@Component({
  selector: 'app-bottom-tab-bar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslocoModule],
  templateUrl: './bottom-tab-bar.component.html',
  styleUrls: ['./bottom-tab-bar.component.css'],
})
export class BottomTabBarComponent implements OnInit, OnDestroy {
  // Toutes les valeurs `label`/`sub` sont des CLÉS i18n.
  readonly tabs: TabItem[] = [
    { label: 'nav.home',        icon: 'home',          route: '/Accueil',  matchPaths: ['/Accueil', '/'] },
    { label: 'tabbar.inbox',    icon: 'chat',          route: '/inbox',    matchPaths: ['/inbox', '/messages'], badge: 'unreadInbox' },
    { label: 'tabbar.pos',      icon: 'point_of_sale', route: '/pos',      matchPaths: ['/pos', '/pos/settings', '/pos/card-payments'] },
    { label: 'tabbar.sales',    icon: 'shopping_cart', route: '/commande', matchPaths: ['/commande', '/clients'] },
    { label: 'tabbar.tab_more', icon: 'apps',          matchPaths: [],     isMore: true },
  ];

  private readonly allMoreItems: MoreItem[] = [
    // Admin — supervision & contrôle (en tête pour les admins)
    { label: 'sidebar.item.supervision',       sub: 'tabbar.more_sub.supervision',       icon: 'shield_moon',         route: '/admin/supervision', tone: 'violet', roles: ['admin'] },
    { label: 'sidebar.item.onboarding_review', sub: 'tabbar.more_sub.onboarding_review', icon: 'how_to_reg',          route: '/admin/onboarding-review', tone: 'violet', roles: ['admin', 'support'] },
    { label: 'sidebar.item.prospection',       sub: 'tabbar.more_sub.prospection',       icon: 'radar',               route: '/admin/prospection', tone: 'violet', roles: ['admin', 'support'] },
    // Marketing (Meta Suite — module unifié)
    { label: 'nav.marketing',                  sub: 'tabbar.more_sub.marketing',         icon: 'campaign',            route: '/marketing',         tone: 'pink' },
    // Social
    { label: 'sidebar.item.social_onboarding', sub: 'tabbar.more_sub.social_onboarding', icon: 'rocket_launch', route: '/social/onboarding', tone: 'pink' },
    { label: 'sidebar.item.social_posts',      sub: 'tabbar.more_sub.social_posts',      icon: 'campaign',      route: '/social/posts',      tone: 'accent' },
    { label: 'nav.chatbot',                    sub: 'tabbar.more_sub.chatbot',           icon: 'smart_toy',     route: '/social/chatbot',    tone: 'violet' },
    { label: 'tabbar.more.social_channels',    sub: 'tabbar.more_sub.social_channels',   icon: 'hub',                 route: '/settings/channels', tone: 'cyan' },
    // Commerce
    { label: 'sidebar.item.catalog',           sub: 'tabbar.more_sub.catalog',           icon: 'inventory_2',         route: '/produits',         tone: 'primary' },
    { label: 'nav.stores',                     sub: 'tabbar.more_sub.stores',            icon: 'storefront',          route: '/Boutique',         tone: 'accent' },
    { label: 'sidebar.item.pos_settings',      sub: 'tabbar.more_sub.pos_settings',      icon: 'manage_accounts',     route: '/pos/settings',     tone: 'gray' },
    { label: 'sidebar.item.pos_card_payments', sub: 'tabbar.more_sub.pos_card_payments', icon: 'credit_score',        route: '/pos/card-payments', tone: 'gray', roles: ['vendor', 'admin'] },
    // Telephonie (centre d'appels)
    { label: 'sidebar.item.callcenter',        sub: 'tabbar.more_sub.callcenter',        icon: 'call',                route: '/telephonie',       tone: 'primary' },
    // Logistique / Inventaire
    { label: 'tabbar.more.inventory',          sub: 'tabbar.more_sub.inventory',         icon: 'warehouse',           route: '/inventaire',       tone: 'cyan' },
    { label: 'tabbar.more.stock_products',     sub: 'tabbar.more_sub.stock_products',    icon: 'inventory_2',         route: '/inventaire/stock', tone: 'primary' },
    { label: 'tabbar.more.stock_moves',        sub: 'tabbar.more_sub.stock_moves',       icon: 'receipt_long',        route: '/inventaire/mouvements', tone: 'accent' },
    // Sites
    { label: 'nav.sites',                      sub: 'tabbar.more_sub.sites',             icon: 'language',            route: '/sites',            tone: 'cyan' },
    // Logistique & outils — le hub Expéditions remplace tunisie-delivery
    // (unification ship01 : transporteurs + livreurs internes au même endroit).
    { label: 'sidebar.item.shipments',         sub: 'tabbar.more_sub.shipments',         icon: 'local_shipping',      route: '/expeditions',      tone: 'violet' },
    { label: 'sidebar.item.couriers',          sub: 'tabbar.more_sub.couriers',          icon: 'two_wheeler',         route: '/livreurs',         tone: 'cyan' },
    { label: 'sidebar.item.cashbox',           sub: 'tabbar.more_sub.cashbox',           icon: 'account_balance_wallet', route: '/Gestionnaire',  tone: 'accent' },
    { label: 'sidebar.item.accounting',        sub: 'tabbar.more_sub.accounting',        icon: 'account_balance',     route: '/comptabilite',     tone: 'violet', roles: ['vendor', 'admin'] },
    { label: 'sidebar.item.kimbyala',          sub: 'tabbar.more_sub.kimbyala',          icon: 'event_note',          route: '/kimbyala',         tone: 'violet', roles: ['vendor', 'admin'] },
    { label: 'sidebar.item.calculator',        sub: 'tabbar.more_sub.calculator',        icon: 'calculate',           route: '/calculator',       tone: 'gray' },
    { label: 'sidebar.item.employees',         sub: 'tabbar.more_sub.employees',         icon: 'badge',               route: '/Employe',          tone: 'violet' },
    { label: 'sidebar.item.activities',        sub: 'tabbar.more_sub.activities',        icon: 'history',             route: '/activities',       tone: 'gray' },
    // Compte
    { label: 'navbar.my_profile',              sub: 'tabbar.more_sub.profile',           icon: 'account_circle',      route: '/profile',          tone: 'primary' },
    { label: 'nav.settings',                   sub: 'tabbar.more_sub.settings',          icon: 'settings',            route: '/settings',         tone: 'gray' },
  ];

  /** Items du panneau « Plus » filtrés par rôle (même logique que la sidebar). */
  get moreItems(): MoreItem[] {
    return this.allMoreItems.filter(
      (it) => !it.roles || it.roles.some((r) => this.auth.hasRole(r)),
    );
  }

  readonly activeRoute = signal<string>('');
  readonly unreadInbox = signal<number>(0);
  readonly moreOpen = signal<boolean>(false);

  private routerSub?: Subscription;
  private statsTimer?: any;

  constructor(private router: Router, private inbox: InboxService, private auth: AuthService) {}

  ngOnInit(): void {
    this.activeRoute.set(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        this.activeRoute.set(event.urlAfterRedirects);
        this.moreOpen.set(false);
      });

    this.refreshStats();
    this.statsTimer = setInterval(() => this.refreshStats(), 60_000);
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    if (this.statsTimer) clearInterval(this.statsTimer);
  }

  isActive(tab: TabItem): boolean {
    if (tab.isMore) return this.moreOpen();
    const url = this.activeRoute();
    return tab.matchPaths.some((p) => url === p || url.startsWith(p + '/'));
  }

  badgeFor(tab: TabItem): number {
    if (tab.badge === 'unreadInbox') return this.unreadInbox();
    return 0;
  }

  go(tab: TabItem): void {
    if (tab.isMore) {
      this.moreOpen.update((v) => !v);
      return;
    }
    this.moreOpen.set(false);
    if (tab.route) this.router.navigateByUrl(tab.route);
  }

  goMore(item: MoreItem): void {
    this.moreOpen.set(false);
    this.router.navigateByUrl(item.route);
  }

  closeMore(): void {
    this.moreOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.moreOpen()) this.moreOpen.set(false);
  }

  private refreshStats(): void {
    this.inbox.stats().subscribe({
      next: (stats) => this.unreadInbox.set(stats.unread || 0),
      error: () => this.unreadInbox.set(0),
    });
  }
}
